import { Head, Link, router } from '@inertiajs/react'
import { Calendar, Target, CheckSquare, Building2, ArrowRight, Mountain, BarChart3, ListChecks, MessageSquare } from 'lucide-react'
import type {
  GuideDashboardProps,
  GuideDashboardAccount,
  GuideDashboardHealth,
  GuideDashboardMyActionItem,
  GuideDashboardMyTopic,
  GuideDashboardMyRock,
} from '../../types'
import Layout from '../../components/Layout'
import StatNumber from '../../components/shared/StatNumber'

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'No date'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function goTo(account: GuideDashboardAccount, path: string) {
  if (account.is_current) {
    router.visit(path)
  } else {
    router.patch('/account/switch_organization', {
      organization_id: account.organization.id,
      redirect_to: path,
    })
  }
}

export default function Index({ accounts = [], my_action_items = [], my_topics = [], my_rocks = [], totals }: Partial<GuideDashboardProps>) {
  const summary = totals ?? {
    organizations: accounts.length,
    upcoming_meetings: 0,
    at_risk_rocks: 0,
    open_action_items: 0,
    my_open_action_items: my_action_items.length,
    my_open_topics: my_topics.length,
    my_active_rocks: my_rocks.length,
    metrics_on_track: 0,
    metrics_off_track: 0,
  }

  const totalRated = summary.metrics_on_track + summary.metrics_off_track

  return (
    <Layout>
      <Head title="All Accounts - Pinnacle" />

      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-text-primary font-heading">All Accounts</h1>
            <p className="text-lg text-text-muted mt-1">
              A unified view of health, scoreboard, meetings, rocks, and action items across every company you guide.
            </p>
          </div>
          <Link
            href="/guide_tenants/new"
            className="flex items-center gap-1.5 px-4 py-2 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90 flex-shrink-0"
          >
            <Building2 className="w-4 h-4" /> Create Account
          </Link>
        </div>

        <div className="flex flex-wrap gap-4">
          <SummaryStat icon={<Building2 className="w-5 h-5" />} value={summary.organizations} label="Companies" className="text-text-primary" />
          <SummaryStat
            icon={<BarChart3 className="w-5 h-5" />}
            value={summary.metrics_on_track}
            label={totalRated > 0 ? `Metrics On Track / ${totalRated}` : 'Metrics On Track'}
            className="text-status-win"
          />
          <SummaryStat icon={<Calendar className="w-5 h-5" />} value={summary.upcoming_meetings} label="Upcoming Meetings" className="text-sea" />
          <SummaryStat icon={<Target className="w-5 h-5" />} value={summary.at_risk_rocks} label="At-Risk Rocks" className="text-status-miss" />
          <SummaryStat icon={<CheckSquare className="w-5 h-5" />} value={summary.open_action_items} label="Open Action Items" className="text-status-warning" />
          <SummaryStat icon={<ListChecks className="w-5 h-5" />} value={summary.my_open_action_items} label="My Open Action Items" className="text-pinnacle-red" />
        </div>

        <MyActionItems items={my_action_items} />

        {/* Task #425: the guide's own Topics/Rocks, sourced only from
            companies where a tenant admin granted user privileges. */}
        {(my_topics.length > 0 || my_rocks.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <MyTopics items={my_topics} />
            <MyRocks items={my_rocks} />
          </div>
        )}

        <div className="space-y-6">
          {accounts.map((account) => (
            <AccountCard key={account.organization.id} account={account} />
          ))}
        </div>
      </div>
    </Layout>
  )
}

function SummaryStat({
  icon,
  value,
  label,
  className,
}: {
  icon: React.ReactNode
  value: number
  label: string
  className: string
}) {
  return (
    <div className="bg-white rounded-xl shadow-card border border-stone/10 p-5 min-w-0 flex items-center gap-4" style={{ flex: '1 1 180px', minWidth: 180 }}>
      <div className={`${className} flex-shrink-0`}>{icon}</div>
      <div className="min-w-0">
        <StatNumber size="md" className={className}>{value}</StatNumber>
        <p className="text-base font-semibold text-text-muted font-sans">{label}</p>
      </div>
    </div>
  )
}

function MyActionItemRow({ item }: { item: GuideDashboardMyActionItem }) {
  const basePath = item.team_id ? `/teams/${item.team_id}/action_items` : '/action_items'
  const targetPath = `${basePath}?highlight=${item.id}`
  return (
    <button
      onClick={() => router.patch('/account/switch_organization', {
        organization_id: item.organization_id,
        redirect_to: targetPath,
      })}
      className="w-full text-left rounded-lg px-4 py-3 hover:bg-[#F8F9FA] transition-colors border border-stone/10"
    >
      <p className="text-base font-semibold text-text-primary truncate font-sans">{item.title}</p>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
        <span className={
          item.overdue
            ? 'text-sm text-status-miss font-semibold'
            : item.due_today
              ? 'text-sm text-status-warning font-semibold'
              : 'text-sm text-text-muted'
        }>
          {item.overdue ? 'Overdue · ' : item.due_today ? 'Due today · ' : 'Due '}{formatDate(item.due_date)}
        </span>
        {item.carried_over && (
          <span className="text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-status-at-risk-bg text-status-at-risk">
            Carried Over
          </span>
        )}
        <span className="inline-flex items-center gap-1 text-sm text-text-muted">
          <Building2 className="w-3.5 h-3.5" />
          {item.organization_name || 'Unknown company'}
        </span>
        {item.team_name && (
          <span className="text-sm text-text-muted">· {item.team_name}</span>
        )}
      </div>
    </button>
  )
}

function MyActionItems({ items }: { items: GuideDashboardMyActionItem[] }) {
  return (
    <section className="bg-white rounded-xl shadow-card border border-stone/10 overflow-hidden">
      <header className="flex items-center gap-3 px-6 py-4 border-b border-stone/10 bg-[#F8F9FA]">
        <ListChecks className="w-6 h-6 text-pinnacle-red flex-shrink-0" />
        <h2 className="text-xl font-bold text-text-primary font-heading">My Action Items</h2>
        <span className="text-sm font-bold text-text-muted">{items.length}</span>
      </header>
      {items.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <Mountain className="w-10 h-10 text-text-muted mx-auto mb-2" />
          <p className="text-lg font-semibold text-text-primary font-sans">Nothing assigned to you</p>
          <p className="text-base text-text-muted">You have no open action items across any company.</p>
        </div>
      ) : (
        <div className="p-5 space-y-2">
          {items.map((item) => (
            <MyActionItemRow key={`${item.organization_id}-${item.id}`} item={item} />
          ))}
        </div>
      )}
    </section>
  )
}

function MyTopicRow({ item }: { item: GuideDashboardMyTopic }) {
  const targetPath = item.team_id ? `/teams/${item.team_id}/topics?highlight=${item.id}` : '/'
  return (
    <button
      onClick={() => router.patch('/account/switch_organization', {
        organization_id: item.organization_id,
        redirect_to: targetPath,
      })}
      className="w-full text-left rounded-lg px-4 py-3 hover:bg-[#F8F9FA] transition-colors border border-stone/10"
    >
      <p className="text-base font-semibold text-text-primary truncate font-sans">{item.title}</p>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
        <span className="text-sm text-text-muted capitalize">{item.status}</span>
        <span className="inline-flex items-center gap-1 text-sm text-text-muted">
          <Building2 className="w-3.5 h-3.5" />
          {item.organization_name || 'Unknown company'}
        </span>
        {item.team_name && (
          <span className="text-sm text-text-muted">· {item.team_name}</span>
        )}
      </div>
    </button>
  )
}

function MyTopics({ items }: { items: GuideDashboardMyTopic[] }) {
  return (
    <section className="bg-white rounded-xl shadow-card border border-stone/10 overflow-hidden">
      <header className="flex items-center gap-3 px-6 py-4 border-b border-stone/10 bg-[#F8F9FA]">
        <MessageSquare className="w-6 h-6 text-text-primary flex-shrink-0" />
        <h2 className="text-xl font-bold text-text-primary font-heading">My Topics</h2>
        <span className="text-sm font-bold text-text-muted">{items.length}</span>
      </header>
      {items.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <Mountain className="w-10 h-10 text-text-muted mx-auto mb-2" />
          <p className="text-lg font-semibold text-text-primary font-sans">No open topics</p>
          <p className="text-base text-text-muted">You own no open topics across any company.</p>
        </div>
      ) : (
        <div className="p-5 space-y-2">
          {items.map((item) => (
            <MyTopicRow key={`${item.organization_id}-${item.id}`} item={item} />
          ))}
        </div>
      )}
    </section>
  )
}

function MyRockRow({ item }: { item: GuideDashboardMyRock }) {
  const targetPath = item.team_id ? `/teams/${item.team_id}/rocks` : '/'
  return (
    <button
      onClick={() => router.patch('/account/switch_organization', {
        organization_id: item.organization_id,
        redirect_to: targetPath,
      })}
      className="w-full text-left rounded-lg px-4 py-3 hover:bg-[#F8F9FA] transition-colors border border-stone/10"
    >
      <p className="text-base font-semibold text-text-primary truncate font-sans">{item.title}</p>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
        <span className={item.overdue ? 'text-sm text-status-miss font-semibold' : 'text-sm text-text-muted'}>
          {item.overdue ? 'Overdue · ' : ''}{Math.round(item.percent_complete)}% complete
          {item.due_date ? ` · Due ${formatDate(item.due_date)}` : ''}
        </span>
        <span className="inline-flex items-center gap-1 text-sm text-text-muted">
          <Building2 className="w-3.5 h-3.5" />
          {item.organization_name || 'Unknown company'}
        </span>
        {item.team_name && (
          <span className="text-sm text-text-muted">· {item.team_name}</span>
        )}
      </div>
    </button>
  )
}

function MyRocks({ items }: { items: GuideDashboardMyRock[] }) {
  return (
    <section className="bg-white rounded-xl shadow-card border border-stone/10 overflow-hidden">
      <header className="flex items-center gap-3 px-6 py-4 border-b border-stone/10 bg-[#F8F9FA]">
        <Target className="w-6 h-6 text-status-warning flex-shrink-0" />
        <h2 className="text-xl font-bold text-text-primary font-heading">My Rocks</h2>
        <span className="text-sm font-bold text-text-muted">{items.length}</span>
      </header>
      {items.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <Mountain className="w-10 h-10 text-text-muted mx-auto mb-2" />
          <p className="text-lg font-semibold text-text-primary font-sans">No active rocks</p>
          <p className="text-base text-text-muted">You own no active rocks across any company.</p>
        </div>
      ) : (
        <div className="p-5 space-y-2">
          {items.map((item) => (
            <MyRockRow key={`${item.organization_id}-${item.id}`} item={item} />
          ))}
        </div>
      )}
    </section>
  )
}

const HEALTH_CONFIG: Record<GuideDashboardHealth, { label: string; className: string }> = {
  on_track: { label: 'On Track', className: 'bg-status-on-track-bg text-status-on-track' },
  at_risk: { label: 'At Risk', className: 'bg-status-at-risk-bg text-status-at-risk' },
  off_track: { label: 'Off Track', className: 'bg-status-off-track-bg text-status-off-track' },
}

function HealthBadge({ health }: { health: GuideDashboardHealth }) {
  const config = HEALTH_CONFIG[health]
  return (
    <span className={`text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full flex-shrink-0 ${config.className}`}>
      {config.label}
    </span>
  )
}

function ScoreboardRollup({ account }: { account: GuideDashboardAccount }) {
  const { on_track, off_track, no_data, total, percent_on_track } = account.scoreboard

  if (total === 0) {
    return (
      <div className="px-6 py-3 border-b border-stone/10 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-text-muted" />
        <span className="text-sm text-text-muted">No weekly metrics yet.</span>
      </div>
    )
  }

  const pct = percent_on_track ?? 0

  return (
    <div className="px-6 py-3 border-b border-stone/10 flex flex-wrap items-center gap-x-5 gap-y-2">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-text-muted" />
        <span className="text-sm font-bold uppercase tracking-widest text-text-muted font-sans">Scoreboard</span>
      </div>
      {percent_on_track !== null && (
        <div className="flex items-center gap-2 min-w-[160px] flex-1 max-w-xs">
          <div className="h-2 flex-1 rounded-full bg-stone/20 overflow-hidden">
            <div className="h-full rounded-full bg-status-on-track" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-sm font-semibold text-text-primary">{pct}%</span>
        </div>
      )}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-status-on-track font-semibold">{on_track} on track</span>
        <span className="text-status-off-track font-semibold">{off_track} off track</span>
        {no_data > 0 && <span className="text-text-muted">{no_data} no data</span>}
      </div>
    </div>
  )
}

function AccountCard({ account }: { account: GuideDashboardAccount }) {
  const isEmpty =
    account.upcoming_meetings.length === 0 &&
    account.at_risk_rocks.length === 0 &&
    account.open_action_items.length === 0

  return (
    <section className="bg-white rounded-xl shadow-card border border-stone/10 overflow-hidden">
      <header className="flex items-center justify-between px-6 py-4 border-b border-stone/10 bg-[#F8F9FA]">
        <div className="flex items-center gap-3 min-w-0">
          <Building2 className="w-6 h-6 text-pinnacle-red flex-shrink-0" />
          <h2 className="text-xl font-bold text-text-primary font-heading truncate">{account.organization.name}</h2>
          <HealthBadge health={account.health} />
          {account.is_current && (
            <span className="text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-charcoal/10 text-text-primary flex-shrink-0">
              Active
            </span>
          )}
        </div>
        {account.is_current ? (
          <Link
            href="/"
            className="flex items-center gap-1.5 text-base font-semibold text-pinnacle-red hover:text-pinnacle-red-dark transition-colors flex-shrink-0"
          >
            Open <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <button
            onClick={() => router.patch('/account/switch_organization', { organization_id: account.organization.id })}
            className="flex items-center gap-1.5 text-base font-semibold text-pinnacle-red hover:text-pinnacle-red-dark transition-colors flex-shrink-0"
          >
            Switch to this account <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </header>

      <ScoreboardRollup account={account} />

      {isEmpty ? (
        <div className="px-6 py-10 text-center">
          <Mountain className="w-10 h-10 text-text-muted mx-auto mb-2" />
          <p className="text-lg font-semibold text-text-primary font-sans">All clear here</p>
          <p className="text-base text-text-muted">No upcoming meetings, at-risk rocks, or open action items.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-stone/10">
          <MeetingsColumn account={account} />
          <RocksColumn account={account} />
          <ActionItemsColumn account={account} />
        </div>
      )}
    </section>
  )
}

function ColumnHeading({ icon, title, count }: { icon: React.ReactNode; title: string; count: number }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h3 className="text-sm font-bold uppercase tracking-widest text-text-muted font-sans">{title}</h3>
      <span className="text-sm font-bold text-text-muted">{count}</span>
    </div>
  )
}

function MeetingsColumn({ account }: { account: GuideDashboardAccount }) {
  const meetings = account.upcoming_meetings
  return (
    <div className="p-5">
      <ColumnHeading icon={<Calendar className="w-4 h-4 text-sea" />} title="Upcoming Meetings" count={meetings.length} />
      {meetings.length === 0 ? (
        <p className="text-base text-text-muted">None scheduled.</p>
      ) : (
        <ul className="space-y-2">
          {meetings.map((m) => (
            <li key={m.id}>
              <button
                onClick={() => goTo(account, `/meetings/${m.id}`)}
                className="w-full text-left rounded-lg px-3 py-2 hover:bg-[#F8F9FA] transition-colors"
              >
                <p className="text-base font-semibold text-text-primary truncate font-sans">{m.name}</p>
                <p className="text-sm text-text-muted">
                  {formatDate(m.scheduled_date)}
                  {m.team_name ? ` · ${m.team_name}` : ''}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function RocksColumn({ account }: { account: GuideDashboardAccount }) {
  const rocks = account.at_risk_rocks
  return (
    <div className="p-5">
      <ColumnHeading icon={<Target className="w-4 h-4 text-status-miss" />} title="At-Risk Rocks" count={rocks.length} />
      {rocks.length === 0 ? (
        <p className="text-base text-text-muted">All rocks on track.</p>
      ) : (
        <ul className="space-y-2">
          {rocks.map((r) => (
            <li key={r.id}>
              <button
                onClick={() => goTo(account, r.team_id ? `/teams/${r.team_id}/rocks` : '/')}
                className="w-full text-left rounded-lg px-3 py-2 hover:bg-[#F8F9FA] transition-colors"
              >
                <p className="text-base font-semibold text-text-primary truncate font-sans">{r.title}</p>
                <p className="text-sm">
                  <span className={r.overdue ? 'text-status-miss font-semibold' : 'text-text-muted'}>
                    {r.overdue ? 'Overdue · ' : ''}{Math.round(r.percent_complete)}% complete
                  </span>
                  {r.team_name ? <span className="text-text-muted"> · {r.team_name}</span> : null}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ActionItemsColumn({ account }: { account: GuideDashboardAccount }) {
  const items = account.open_action_items
  return (
    <div className="p-5">
      <ColumnHeading icon={<CheckSquare className="w-4 h-4 text-status-warning" />} title="Open Action Items" count={items.length} />
      {items.length === 0 ? (
        <p className="text-base text-text-muted">Nothing open.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li key={a.id}>
              <button
                onClick={() => goTo(account, '/action_items')}
                className="w-full text-left rounded-lg px-3 py-2 hover:bg-[#F8F9FA] transition-colors"
              >
                <p className="text-base font-semibold text-text-primary truncate font-sans">{a.title}</p>
                <p className="text-sm">
                  <span className={a.overdue ? 'text-status-miss font-semibold' : a.due_today ? 'text-status-warning font-semibold' : 'text-text-muted'}>
                    {a.overdue ? 'Overdue · ' : a.due_today ? 'Due today · ' : 'Due '}{formatDate(a.due_date)}
                  </span>
                  {a.team_name ? <span className="text-text-muted"> · {a.team_name}</span> : null}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
